import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { RouterOutlet, NavigationEnd, Router } from '@angular/router';
import { AuthService } from './core/services/auth.service';
import { UserService } from './core/services/user.service';
import { ThemeService } from './core/services/theme.service';
import { NotificationService } from './core/services/notification.service';
import { AppUpdateService } from './core/services/app-update.service';
import { ResponsiveHelperComponent } from './shared/components/responsive-helper/responsive-helper.component';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Toast } from 'primeng/toast';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet, ResponsiveHelperComponent, CommonModule, TranslocoModule, Toast],
})
export class AppComponent implements OnInit, OnDestroy {
  themeService = inject(ThemeService);
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  private appUpdateService = inject(AppUpdateService);

  private langSub?: Subscription;
  private routerSub?: Subscription;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  ngOnInit() {
    // Before anything else: it only arms a listener, and a deploy landing mid-session is
    // exactly when the rest of this would start failing.
    this.appUpdateService.start();
    this.refreshUserData();
    this.initializeWebSocketConnection();
    this.requestBrowserNotificationPermission();
    // DirectionService will handle RTL automatically
  }

  ngOnDestroy() {
    this.langSub?.unsubscribe();
    this.routerSub?.unsubscribe();
    this.notificationService.disconnect();
  }

  /**
   * Initialize WebSocket connection if user is authenticated
   * Also listen to route changes to reconnect if needed
   */
  private initializeWebSocketConnection(): void {
    // Connect to WebSocket only if user is authenticated
    if (this.authService.isAuthenticated()) {
      this.notificationService.connect();
    }

    // Listen to route changes to ensure WebSocket stays connected
    this.routerSub = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      // Reconnect if user is authenticated but WebSocket is disconnected
      if (this.authService.isAuthenticated() && !this.notificationService.isConnected()) {
        this.notificationService.connect();
      }
    });
  }

  /**
   * Request browser notification permission for desktop notifications
   */
  private requestBrowserNotificationPermission(): void {
    if ('Notification' in window && Notification.permission === 'default') {
      // The answer is read from Notification.permission wherever it matters, so nothing is
      // waiting on this promise.
      void Notification.requestPermission();
    }
  }

  private refreshUserData() {
    // Only proceed if user is logged in
    const currentUser = this.authService.getCurrentUser();
    if (currentUser && currentUser.username) {
      // Get fresh user data
      this.userService
        .findByUsername(currentUser.username)
        .then(async (data: any) => {
          // Check if remember me was enabled (get from storage)
          const rememberMe = localStorage.getItem('rememberMe') === 'true';
          // Update user data in storage
          this.authService.saveUser(data, rememberMe);
          // Update profile picture if available
          //await this.authService.getProfilePicture(data..attachment);
        })
        .catch((error) => {
          console.error('Error refreshing user data:', error);
        });
    }
  }
}
